/**
 * Content Operations Center — campaign-first review UX.
 *
 * Header: operational counters + campaigns awaiting review.
 * Left column: Campaign Pack cards, each with status chips + one-click bulk actions.
 * Selected assets expand in place so review stays anchored to the row.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { fetchPackAssets, fetchPackSummary, fetchAssetLifecycle, HeroImagePicker, LifecycleActions, RenderedImagePreview, RenderedVideoPreview, VideoRenderController, regenerateAsset, renderImageAsset, editAsset, BatchActionBar, BatchSelectionProvider, useBatchSelection, CampaignChecklist, getAssetLabel, type PackSummary } from "@/entities/ai-studio";
import type { CampaignPack, GeneratedAsset } from "@/entities/ai-studio";
import { useCurrentWorkspace } from "@/entities/workspace";
import { fetchMe } from "@/entities/workspace/api";
import {
  useCampaignRunSummary,
  useReviewInventory,
  invalidateCampaignLifecycle,
} from "@/entities/campaign-run";
import { CampaignRunSummaryCard } from "@/widgets/CampaignRunSummary/CampaignRunSummaryCard";
import { WorkflowStages } from "@/widgets/WorkflowStages/WorkflowStages";
import {
  STATUS_META,
  addComment,
  approvePack,
  archivePack,
  bulkApprove,
  bulkArchive,
  bulkReturn,
  getAssetHistory,
  listVersions,
  opsSummary,
  restoreVersion,
  returnPack,
  transitionAsset,
} from "@/entities/ops";
import type { AssetStatus, CommentType, HistoryEvent, OpsSummary, PackAwaiting } from "@/entities/ops";
import { deriveBulkCounts, summariseBulkCounts } from "@/entities/ops/counts";
import { useUpgradePrompt } from "@/features/entitlements/useEntitlements";
import { http } from "@/shared/api/http";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Skeleton } from "@/shared/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";


const listPacks = (workspaceId: string): Promise<CampaignPack[]> =>
  http(`/workspaces/${workspaceId}/campaign-packs`);

// Patch 16 · Content Review URL-state filters
type StatusFilter = "all" | "needs-review" | "draft" | "approved" | "scheduled";
type TypeFilter = "all" | "image" | "video" | "email" | "social";
const STATUS_FILTERS: StatusFilter[] = ["all", "needs-review", "draft", "approved", "scheduled"];
const TYPE_FILTERS: TypeFilter[] = ["all", "image", "video", "email", "social"];

const STATUS_FILTER_LABEL: Record<StatusFilter, string> = {
  all:            "All",
  "needs-review": "Needs review",
  draft:          "Draft",
  approved:       "Approved",
  scheduled:      "Scheduled",
};
const TYPE_FILTER_LABEL: Record<TypeFilter, string> = {
  all:    "All types",
  image:  "Image",
  video:  "Video",
  email:  "Email",
  social: "Social",
};

// Maps our URL-friendly status keys to the backend asset status codes.
const STATUS_FILTER_TO_ASSET: Record<Exclude<StatusFilter, "all">, string> = {
  "needs-review": "needs_review",
  draft:          "draft",
  approved:       "approved",
  scheduled:      "scheduled",
};

// Rough content-type keyword mapping — asset content_type values are
// varied strings, so we normalize by substring match.
const TYPE_MATCHERS: Record<Exclude<TypeFilter, "all">, RegExp> = {
  image:  /image|graphic|flyer|thumbnail|photo/i,
  video:  /video|reel|clip|short/i,
  email:  /email|newsletter/i,
  social: /social|instagram|tiktok|linkedin|twitter|facebook|post/i,
};


export function ContentOpsPage(): JSX.Element {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  const [openAssetId, setOpenAssetId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activePackId, setActivePackId] = useState<string | null>(null);

  // Patch 35.3b · admin flag hoisted for the batch preflight modal
  // (founders see projected spend; standard users see the safe copy).
  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const isAdmin = meQuery.data?.is_dev_user === true;

  // Patch 16 · URL-state continuity for the filter row. `?status=` and
  // `?type=` drive both the initial render and every subsequent state
  // change. Browser Back/Forward "just works" because we use
  // useSearchParams (no full-page remounts, no duplicate API calls).
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = (searchParams.get("status") ?? "all").toLowerCase();
  const typeParam = (searchParams.get("type") ?? "all").toLowerCase();
  const activeStatus: StatusFilter = STATUS_FILTERS.includes(statusParam as StatusFilter)
    ? (statusParam as StatusFilter)
    : "all";
  const activeType: TypeFilter = TYPE_FILTERS.includes(typeParam as TypeFilter)
    ? (typeParam as TypeFilter)
    : "all";

  const setStatusFilter = (next: StatusFilter) => {
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("status"); else params.set("status", next);
    setSearchParams(params, { replace: false });
  };
  const setTypeFilter = (next: TypeFilter) => {
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("type"); else params.set("type", next);
    setSearchParams(params, { replace: false });
  };

  const summary = useQuery({
    queryKey: ["ops-summary", ws?.id],
    queryFn: () => opsSummary(ws!.id),
    enabled: !!ws,
    refetchInterval: 5000,
  });
  const packs = useQuery({
    queryKey: ["ops-packs", ws?.id],
    queryFn: () => listPacks(ws!.id),
    enabled: !!ws,
    refetchInterval: 5000,
  });

  // Patch 34.2.1b/c · Canonical CampaignRun projection.
  const campaignRunSummary = useCampaignRunSummary(ws?.id ?? null);
  // Patch 34.2.1d · The ACTUAL validated, reviewable ai_asset inventory —
  // the third independent signal used by the integrity guardrail below.
  const reviewInventory = useReviewInventory(ws?.id ?? null);

  useEffect(() => {
    setOpenAssetId(null);
    setSelectedIds(new Set());
    setActivePackId(null);
  }, [ws?.id]);

  const openAssets = useQuery({
    queryKey: ["ops-pack-assets", activePackId],
    queryFn: () => fetchPackAssets(ws!.id, activePackId!),
    enabled: !!activePackId && !!ws,
  });

  const openAsset = useMemo(
    () => openAssets.data?.find((a) => a.id === openAssetId) ?? null,
    [openAssets.data, openAssetId],
  );

  // Filter derived lists — must not trigger any refetch. Purely visual.
  const visiblePacks = useMemo(() => {
    const all = packs.data ?? [];
    if (activeStatus === "all") return all;
    // A pack is a match when at least one of its assets is in the target
    // status. We use the pack's own asset counts if available; otherwise
    // fall back to pack.status.
    const target = STATUS_FILTER_TO_ASSET[activeStatus];
    return all.filter((p) => {
      const counts = (p as unknown as { asset_status_counts?: Record<string, number> }).asset_status_counts;
      if (counts && typeof counts[target] === "number") return counts[target] > 0;
      return p.status === target;
    });
  }, [packs.data, activeStatus]);

  const visibleAssets = useMemo(() => {
    const all = openAssets.data ?? [];
    const byStatus = activeStatus === "all"
      ? all
      : all.filter((a) => a.status === STATUS_FILTER_TO_ASSET[activeStatus]);
    if (activeType === "all") return byStatus;
    const re = TYPE_MATCHERS[activeType];
    return byStatus.filter((a) => re.test(a.content_type ?? ""));
  }, [openAssets.data, activeStatus, activeType]);

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ["ops-summary", ws?.id] });
    void qc.invalidateQueries({ queryKey: ["ops-packs", ws?.id] });
    if (activePackId) void qc.invalidateQueries({ queryKey: ["ops-pack-assets", activePackId] });
    if (openAssetId) {
      void qc.invalidateQueries({ queryKey: ["ops-asset-history", openAssetId] });
      void qc.invalidateQueries({ queryKey: ["ops-asset-versions", openAsset?.permanent_asset_id] });
    }
    // Patch 34.2.1b · Every lifecycle mutation on Review MUST also
    // invalidate the canonical CampaignRun projection so the Home page,
    // Dashboard Campaign Pulse, and future Vault/Publishing surfaces
    // never lag the Review queue.
    if (ws) invalidateCampaignLifecycle(qc, ws.id);
  };

  const approvePackMut = useMutation({
    mutationFn: (packId: string) => approvePack(ws!.id, packId),
    onSuccess: (r) => {
      // Patch 37.4.43 · Founder-honest toast — every outcome bucket
      // rendered ("3 approved · 2 already approved · 1 blocked
      // (render first)"). The old bare "Approved N" reported N=0 for
      // a batch of spec-blocked heads and looked identical to a
      // silent failure.
      const counts = deriveBulkCounts(r);
      const summary = summariseBulkCounts(counts, {
        verbPast: "Approved",
        alreadyLabel: "already approved",
      });
      if (counts.blocked_spec > 0 || counts.error > 0) {
        toast.warning(summary);
      } else {
        toast.success(summary);
      }
      invalidateAll();
    },
    onError: (e: Error) => toast.error(e.message || "Approve pack failed"),
  });
  const returnPackMut = useMutation({
    mutationFn: (packId: string) => returnPack(ws!.id, packId, "Please revise."),
    onSuccess: (r) => {
      const counts = deriveBulkCounts(r);
      const summary = summariseBulkCounts(counts, {
        verbPast: "Returned",
        alreadyLabel: "already in review",
      });
      toast.success(summary);
      invalidateAll();
    },
    onError: (e: Error) => toast.error(e.message || "Return pack failed"),
  });
  const archivePackMut = useMutation({
    mutationFn: (packId: string) => archivePack(ws!.id, packId),
    onSuccess: (r) => {
      const counts = deriveBulkCounts(r);
      const summary = summariseBulkCounts(counts, {
        verbPast: "Archived",
        alreadyLabel: "already archived",
      });
      toast.success(summary);
      invalidateAll();
      setActivePackId(null);
    },
    onError: (e: Error) => toast.error(e.message || "Archive pack failed"),
  });

  const bulkApproveMut = useMutation({
    mutationFn: () => bulkApprove(ws!.id, Array.from(selectedIds)),
    onSuccess: () => { toast.success("Bulk approved"); setSelectedIds(new Set()); invalidateAll(); },
  });
  const bulkReturnMut = useMutation({
    mutationFn: () => bulkReturn(ws!.id, Array.from(selectedIds), "Please revise."),
    onSuccess: () => { toast.success("Bulk returned"); setSelectedIds(new Set()); invalidateAll(); },
  });
  const bulkArchiveMut = useMutation({
    mutationFn: () => bulkArchive(ws!.id, Array.from(selectedIds)),
    onSuccess: () => { toast.success("Bulk archived"); setSelectedIds(new Set()); invalidateAll(); },
  });

  if (!ws) return <div className="mx-auto max-w-3xl px-6 py-16 text-center text-muted-foreground">Select a workspace.</div>;

  // Patch 34.2.1b/c/d · Integrity Mismatch guardrail. THREE genuinely
  // independent signals must agree for the Review queue to be trusted:
  //   • canonical draft count — from CampaignRun projection
  //     (`GET /campaign-runs/summary`).
  //   • operational summary draft count — from `opsSummary()` used to
  //     render the six top counters.
  //   • actual reviewable asset inventory — from the dedicated
  //     `GET /campaign-runs/review-inventory` endpoint (Patch 34.2.1d).
  //     Counts real `ai_assets` heads with status ∈ {draft, needs_review},
  //     NOT `campaign_pack.total_assets` metadata. This is the only
  //     signal that catches the "counters agree, nothing rendered"
  //     failure mode.
  //
  // Any pairwise disagreement (once ALL THREE queries have resolved)
  // surfaces a loud banner.
  const canonicalDrafts = campaignRunSummary.data?.counters.draft ?? null;
  const observedDraftCounter = summary.data?.counters.find(
    (c) => c.status === "draft",
  );
  const observedDrafts = observedDraftCounter?.count ?? null;
  const actualReviewable = reviewInventory.data?.draft ?? null;

  const allLoaded =
    canonicalDrafts !== null &&
    observedDrafts !== null &&
    actualReviewable !== null;
  const drift = allLoaded && canonicalDrafts !== observedDrafts;
  const hiddenAssets =
    allLoaded &&
    (canonicalDrafts > 0 || observedDrafts > 0) &&
    actualReviewable === 0;
  const inventoryDrift =
    allLoaded && canonicalDrafts !== actualReviewable;
  const integrityMismatch = drift || hiddenAssets || inventoryDrift;

  return (
    <BatchSelectionProvider>
    <div className="mx-auto max-w-7xl px-6 py-10" data-testid="ops-page">
      <WorkflowStages current="review" />
      <Header ws={ws} />

      {/* Patch 35.3b · Sticky batch action bar. Renders only when at
          least one image prompt is selected in the currently expanded
          pack. `useCurrentWorkspaceIdForBatch` hoists ws id from the
          provider. */}
      <BatchActionBarPortal workspaceId={ws.id} isAdmin={isAdmin} />

      {/* Patch 34.2.1b · Canonical Campaign Pulse — the ONE source of
          truth for campaign counts. Mounted above the legacy operational
          dashboard so any drift is immediately visible. */}
      <div className="mb-6">
        <CampaignRunSummaryCard workspaceId={ws.id} />
      </div>

      {/* Patch 34.2.1b/c · Integrity Mismatch guardrail. Renders only
          when the canonical projection disagrees with the Review queue
          numbers OR when canonical reports drafts but zero assets are
          reviewable on-screen. During initial load we render nothing —
          never flash a false positive. */}
      {/* Patch 34.2.1f · Inventory unavailable — the canonical asset
          model could not validate any candidate row (HTTP 503 from
          /campaign-runs/review-inventory). Do NOT interpret this as
          "empty inventory" — render a recovery state that lets the
          founder retry and report. */}
      {reviewInventory.isError && (
        <Card
          data-testid="ops-inventory-unavailable"
          className="mb-6 border-red-500/60 bg-red-500/10"
        >
          <CardContent className="p-4 text-sm">
            <div
              data-testid="ops-inventory-unavailable-title"
              className="font-semibold text-red-800"
            >
              Review inventory is temporarily unavailable.
            </div>
            <p className="mt-1 text-xs text-red-900/80">
              The canonical asset model could not validate any candidate
              row. Your drafts are not lost — the counters above are
              still tracked. Do NOT publish from this queue until the
              inventory is back online.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                data-testid="ops-inventory-unavailable-retry"
                size="sm"
                variant="outline"
                onClick={() => void reviewInventory.refetch()}
              >
                Retry
              </Button>
              <Button
                asChild
                size="sm"
                variant="ghost"
                data-testid="ops-inventory-unavailable-report"
              >
                <a href="mailto:support@orkexra.com?subject=Review%20inventory%20unavailable">
                  Report Issue
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Patch 34.2.1e/f · Orphan warning — reviewable assets not linked
          to any Campaign Pack. Gated on `reviewInventory` data actually
          loaded so nothing flashes during initial paint. */}
      {reviewInventory.data && reviewInventory.data.orphan_count > 0 && (
        <Card
          data-testid="ops-orphan-warning"
          className="mb-6 border-amber-500/60 bg-amber-500/10"
        >
          <CardContent className="p-4 text-sm">
            <div
              data-testid="ops-orphan-warning-title"
              className="font-semibold text-amber-800"
            >
              <span data-testid="ops-orphan-warning-count">
                {reviewInventory.data.orphan_count}
              </span>{" "}
              reviewable draft{reviewInventory.data.orphan_count === 1 ? "" : "s"}{" "}
              {reviewInventory.data.orphan_count === 1 ? "has" : "have"} no
              Campaign Pack reference.
            </div>
            <p className="mt-1 text-xs text-amber-900/80">
              These are legacy recovery assets and must not be treated as
              a healthy campaign. Write-time invariants in Patch 34.2.2
              will refuse new orphan drafts.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Patch 34.2.1f · Dangling warning — assets that reference a
          Pack ID which no longer exists. */}
      {reviewInventory.data && reviewInventory.data.dangling_pack_count > 0 && (
        <Card
          data-testid="ops-dangling-warning"
          className="mb-6 border-amber-500/60 bg-amber-500/10"
        >
          <CardContent className="p-4 text-sm">
            <div
              data-testid="ops-dangling-warning-title"
              className="font-semibold text-amber-800"
            >
              <span data-testid="ops-dangling-warning-count">
                {reviewInventory.data.dangling_pack_count}
              </span>{" "}
              reviewable draft{reviewInventory.data.dangling_pack_count === 1 ? "" : "s"}{" "}
              reference{reviewInventory.data.dangling_pack_count === 1 ? "s" : ""}{" "}
              Campaign Pack{reviewInventory.data.dangling_pack_count === 1 ? " that" : "s that"}{" "}
              no longer exist{reviewInventory.data.dangling_pack_count === 1 ? "s" : ""}.
            </div>
            <p className="mt-1 text-xs text-amber-900/80">
              The Pack rows may have been deleted after the assets were
              generated. Do NOT publish these until they are re-linked.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Patch 34.2.1f · Cross-workspace linkage — a SECURITY + integrity
          incident. Render loud red and surface the safe incident id so
          the founder / admin can escalate. */}
      {reviewInventory.data && reviewInventory.data.cross_workspace_pack_count > 0 && (
        <Card
          data-testid="ops-cross-workspace-warning"
          className="mb-6 border-red-500/70 bg-red-500/10"
        >
          <CardContent className="p-4 text-sm">
            <div
              data-testid="ops-cross-workspace-warning-title"
              className="font-semibold text-red-800"
            >
              Cross-workspace linkage detected —{" "}
              <span data-testid="ops-cross-workspace-warning-count">
                {reviewInventory.data.cross_workspace_pack_count}
              </span>{" "}
              reviewable draft{reviewInventory.data.cross_workspace_pack_count === 1 ? "" : "s"}{" "}
              reference{reviewInventory.data.cross_workspace_pack_count === 1 ? "s" : ""}{" "}
              a Campaign Pack outside this workspace.
            </div>
            <p className="mt-1 text-xs text-red-900/80">
              This is a security and integrity incident. Do NOT approve
              these drafts. Contact support with the reference below.
            </p>
            {reviewInventory.data.incident_id && (
              <p
                className="mt-2 text-xs font-mono text-red-900/80"
                data-testid="ops-cross-workspace-warning-incident"
              >
                Incident: {reviewInventory.data.incident_id}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {integrityMismatch && (
        <Card
          data-testid="ops-integrity-mismatch"
          className="mb-6 border-red-500/60 bg-red-500/10"
        >
          <CardContent className="p-4 text-sm">
            <div
              data-testid="ops-integrity-mismatch-title"
              className="font-semibold text-red-800"
            >
              {hiddenAssets
                ? "Campaign state reports drafts, but the review endpoint returned zero reviewable assets."
                : drift && inventoryDrift
                ? "Integrity mismatch — canonical, operational, and actual review inventory all disagree."
                : drift
                ? "Integrity mismatch — Review queue disagrees with canonical projection."
                : "Integrity mismatch — canonical projection disagrees with the actual reviewable inventory."}
            </div>
            <ul className="mt-2 space-y-0.5 text-xs text-red-900/80">
              <li>
                Campaign state (canonical projection):{" "}
                <strong data-testid="ops-integrity-mismatch-canonical">
                  {canonicalDrafts}
                </strong>{" "}
                draft{canonicalDrafts === 1 ? "" : "s"}
              </li>
              <li>
                Operations (ops summary):{" "}
                <strong data-testid="ops-integrity-mismatch-observed">
                  {observedDrafts}
                </strong>{" "}
                draft{observedDrafts === 1 ? "" : "s"}
              </li>
              <li>
                Actual reviewable inventory (validated ai_asset heads):{" "}
                <strong data-testid="ops-integrity-mismatch-inventory">
                  {actualReviewable}
                </strong>
                {" "}·{" "}
                <span data-testid="ops-integrity-mismatch-inventory-packs">
                  {reviewInventory.data?.pack_count ?? 0}
                </span>{" "}
                pack{(reviewInventory.data?.pack_count ?? 0) === 1 ? "" : "s"}
              </li>
              {reviewInventory.data && reviewInventory.data.rows_skipped > 0 && (
                <li data-testid="ops-integrity-mismatch-skip-reasons">
                  Excluded from review:{" "}
                  <strong>{reviewInventory.data.rows_skipped}</strong>{" "}
                  ({Object.entries(reviewInventory.data.skip_reasons ?? {})
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([reason, count]) => `${reason.split("_").join(" ")}: ${count}`)
                    .join(" · ") || "reason unavailable"})
                </li>
              )}
            </ul>
            <p className="mt-2 text-xs text-red-900/80">
              At least one source is stale. Retry, or inspect campaign
              health — do NOT publish from this queue until it clears.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                data-testid="ops-integrity-mismatch-refresh"
                size="sm"
                variant="outline"
                onClick={() => {
                  void qc.invalidateQueries({ queryKey: ["ops-summary", ws.id] });
                  void qc.invalidateQueries({ queryKey: ["ops-packs", ws.id] });
                  invalidateCampaignLifecycle(qc, ws.id);
                }}
              >
                Retry
              </Button>
              <Button
                asChild
                size="sm"
                variant="outline"
                data-testid="ops-integrity-mismatch-campaign-health"
              >
                <Link to="/campaigns">View Campaign Health</Link>
              </Button>
              <Button
                asChild
                size="sm"
                variant="ghost"
                data-testid="ops-integrity-mismatch-report"
              >
                <a href="mailto:support@orkexra.com?subject=Content%20Review%20integrity%20mismatch">
                  Report Issue
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <SummaryHeader summary={summary.data} loading={summary.isLoading} />

      <div className="mt-8">
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="ds-headline ds-headline--h2">Campaigns</h2>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{selectedIds.size} selected</span>
                <Button data-testid="ops-bulk-approve" size="sm" onClick={() => bulkApproveMut.mutate()}>Approve</Button>
                <Button data-testid="ops-bulk-return" size="sm" variant="outline" onClick={() => bulkReturnMut.mutate()}>Return</Button>
                <Button data-testid="ops-bulk-archive" size="sm" variant="ghost" onClick={() => bulkArchiveMut.mutate()}>Archive</Button>
              </div>
            )}
          </div>

          <FilterRow
            activeStatus={activeStatus}
            activeType={activeType}
            onStatus={setStatusFilter}
            onType={setTypeFilter}
          />

          {packs.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (visiblePacks.length === 0) ? (
            <Card className="border-dashed"><CardContent className="p-6 text-center text-xs text-muted-foreground" data-testid="ops-empty">
              {(packs.data ?? []).length === 0
                ? "No campaign packs yet. Head to AI Studio to generate one."
                : "No packs match the current filters."}
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {visiblePacks.map((p) => (
                <PackCard
                  key={p.id}
                  pack={p}
                  isActive={activePackId === p.id}
                  onToggle={() => setActivePackId(activePackId === p.id ? null : p.id)}
                  onApproveAll={() => approvePackMut.mutate(p.id)}
                  onReturnAll={() => returnPackMut.mutate(p.id)}
                  onArchiveAll={() => archivePackMut.mutate(p.id)}
                  assets={activePackId === p.id ? visibleAssets : []}
                  selectedIds={selectedIds}
                  openAssetId={openAssetId}
                  onSelectAsset={(id, on) =>
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (on) next.add(id);
                      else next.delete(id);
                      return next;
                    })
                  }
                  onOpenAsset={setOpenAssetId}
                  onCloseAsset={() => setOpenAssetId(null)}
                  onTransition={async (asset, to) => {
                    try {
                      await transitionAsset(ws.id, asset.id, to);
                      toast.success(`Moved to ${STATUS_META[to].label}`);
                      invalidateAll();
                    } catch (e: unknown) {
                      toast.error((e as Error).message || "Transition failed");
                    }
                  }}
                />
              ))}
            </div>
          )}
        </section>

      </div>
    </div>
    </BatchSelectionProvider>
  );
}

// Patch 35.3b · thin wrapper so the batch bar can live at the ops
// page's top level while still consuming the provider's context.
function BatchActionBarPortal({ workspaceId, isAdmin }: { workspaceId: string; isAdmin: boolean }): JSX.Element {
  const { activePackId } = useBatchSelection();
  return <BatchActionBar workspaceId={workspaceId} packId={activePackId} isAdmin={isAdmin} />;
}

// ── Sub-components ────────────────────────────────────────────────
function FilterRow({
  activeStatus, activeType, onStatus, onType,
}: {
  activeStatus: StatusFilter;
  activeType: TypeFilter;
  onStatus: (v: StatusFilter) => void;
  onType: (v: TypeFilter) => void;
}): JSX.Element {
  return (
    <div
      data-testid="ops-filter-row"
      className="flex flex-col gap-3 rounded-lg border border-border/60 bg-background/40 p-3 md:flex-row md:items-center md:justify-between"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Status</span>
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`ops-filter-status-${s}`}
            onClick={() => onStatus(s)}
            aria-pressed={activeStatus === s}
            className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition ${
              activeStatus === s
                ? "border-amber-500/50 bg-amber-500/10 text-foreground"
                : "border-border/60 text-muted-foreground hover:border-border hover:text-foreground"
            }`}
          >
            {STATUS_FILTER_LABEL[s]}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Type</span>
        {TYPE_FILTERS.map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`ops-filter-type-${t}`}
            onClick={() => onType(t)}
            aria-pressed={activeType === t}
            className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition ${
              activeType === t
                ? "border-amber-500/50 bg-amber-500/10 text-foreground"
                : "border-border/60 text-muted-foreground hover:border-border hover:text-foreground"
            }`}
          >
            {TYPE_FILTER_LABEL[t]}
          </button>
        ))}
      </div>
    </div>
  );
}

function Header({ ws }: { ws: { slug: string; profile: { display_name: string; primary_color: string } } }): JSX.Element {
  return (
    <div className="mb-6 space-y-2">
      <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/60 px-3 py-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: ws.profile.primary_color }} />
        {ws.slug} · Content Review
      </div>
      <h1
        data-testid="ops-title"
        className="text-4xl font-semibold tracking-tight sm:text-5xl"
        style={{ fontFamily: "var(--font-operational, Inter, sans-serif)" }}
      >
        Content Review
      </h1>
      <p className="max-w-2xl text-sm text-muted-foreground" data-testid="ops-subtitle">
        Every asset your Marketing Department has produced appears here for approval, feedback, revision, or archive.
      </p>
    </div>
  );
}

function SummaryHeader({ summary, loading }: { summary?: OpsSummary; loading: boolean }): JSX.Element {
  return (
    <Card className="border-border/70 bg-card/70 backdrop-blur">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm uppercase tracking-widest text-muted-foreground">Operational Dashboard</CardTitle>
          <span data-testid="ops-avg-review-time" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            avg review time · {summary?.average_review_time ?? "—"}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
          {(loading || !summary ? Array.from({ length: 6 }) : summary.counters).map((c, i) => {
            if (!c || loading) return <Skeleton key={i} className="h-16 w-full" />;
            const counter = c as OpsSummary["counters"][number];
            return (
              <div
                key={counter.status}
                data-testid={`ops-count-${counter.status.replace("_", "-")}`}
                className="rounded-md border border-border/60 bg-background/40 p-3"
              >
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{counter.label}</div>
                <div className="mt-1 ds-headline ds-headline--h2">{counter.count}</div>
              </div>
            );
          })}
        </div>

        {(summary?.campaigns_awaiting_review ?? []).length > 0 && (
          <div className="mt-4" data-testid="ops-campaigns-awaiting-review">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Campaigns awaiting review</Label>
            <div className="mt-2 grid gap-2 md:grid-cols-3">
              {(summary?.campaigns_awaiting_review ?? []).slice(0, 6).map((c: PackAwaiting) => (
                <div key={c.id} className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs">
                  <div className="line-clamp-1 font-medium">{c.campaign_name_resolved ?? "Campaign"}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {c.needs_review_count}/{c.total_assets} awaiting review
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PackCard({
  pack, isActive, onToggle, onApproveAll, onReturnAll, onArchiveAll,
  assets, selectedIds, openAssetId, onSelectAsset, onOpenAsset, onCloseAsset,
  onTransition,
}: {
  pack: CampaignPack; isActive: boolean; onToggle: () => void;
  onApproveAll: () => void; onReturnAll: () => void; onArchiveAll: () => void;
  assets: GeneratedAsset[]; selectedIds: Set<string>;
  openAssetId: string | null;
  onSelectAsset: (id: string, on: boolean) => void;
  onOpenAsset: (id: string) => void;
  onCloseAsset: () => void;
  onTransition: (asset: GeneratedAsset, to: AssetStatus) => Promise<void>;
}): JSX.Element {
  // Patch 37.4.43 · Live counts + Campaign name resolved by the server.
  const liveTotal = pack.live_total ?? pack.total_assets;
  const displayName = pack.campaign_name_resolved ?? "Campaign";
  const draftCount = pack.live_by_status?.draft ?? 0;
  const reviewCount = pack.live_by_status?.needs_review ?? 0;
  // Approve pack is only meaningful when there is something to approve.
  // A stale-status pack (e.g. Content Ops still shows "draft" while
  // every head is already approved) would otherwise fire an empty
  // batch and toast "Approved 0 assets" — the 37.4.42 iter-78 symptom.
  const approveEligible = draftCount + reviewCount > 0;
  const individualReviewRequired = approveEligible;
  return (
    <Card data-testid={`ops-pack-card-${pack.id}`} className="border-border/70 bg-card/70">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <button className="min-w-0 flex-1 text-left" onClick={onToggle}>
            <CardTitle className="text-base" data-testid={`ops-pack-card-title-${pack.id}`}>{displayName}</CardTitle>
            <CardDescription className="text-xs" data-testid={`ops-pack-live-total-${pack.id}`}>
              {pack.preset ?? "custom"} · {liveTotal} assets{pack.total_cost_usd != null ? ` · $${pack.total_cost_usd.toFixed(4)}` : ""}
            </CardDescription>
          </button>
          <Badge data-testid={`ops-pack-status-${pack.id}`} variant="outline" className="font-mono text-[10px] uppercase tracking-widest">
            {pack.status}
          </Badge>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            data-testid={`ops-pack-toggle-assets-${pack.id}`}
            size="sm"
            variant={isActive ? "secondary" : "default"}
            onClick={onToggle}
          >
            {isActive ? "Hide assets" : `Review ${liveTotal} asset${liveTotal === 1 ? "" : "s"}`}
          </Button>
          {/* Patch 35.1c hotfix — hide bulk actions once the pack has
              graduated past draft/partial. `Approve pack` on an already
              approved/scheduled/published pack is a no-op that misleads
              the founder into thinking approval hasn't landed yet. */}
          {(pack.status === "draft" || pack.status === "partial" || pack.status === "needs_review") && (
            <>
              {/* Milestone 37.3 · Start guided Review Session for this
                  pack. Lives beside the bulk "Approve pack" affordance
                  so founders can choose the workflow that fits — list
                  or mission. */}
              <Link
                to={`/campaigns/${pack.id}/review-session`}
                data-testid={`ops-pack-start-review-session-${pack.id}`}
                className="inline-flex items-center rounded-md border border-cyan-400/40 bg-cyan-400/10 px-3 py-1.5 text-xs font-medium text-cyan-100 hover:bg-cyan-400/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 transition"
              >
                Guided review →
              </Link>
              <Button
                data-testid={`ops-pack-approve-all-${pack.id}`}
                size="sm"
                onClick={onApproveAll}
                disabled={!approveEligible || individualReviewRequired}
                title={individualReviewRequired ? "Review each asset before approving the campaign." : "No assets eligible — every head is already approved or scheduled."}
              >
                {individualReviewRequired ? "Review required" : "Approve pack"}
              </Button>
            </>
          )}
          {(pack.status === "draft" || pack.status === "partial" || pack.status === "approved") && (
            <Button data-testid={`ops-pack-return-all-${pack.id}`} size="sm" variant="outline" onClick={onReturnAll}>Return pack</Button>
          )}
          {pack.status !== "archived" && (
            <Button data-testid={`ops-pack-archive-all-${pack.id}`} size="sm" variant="ghost" onClick={onArchiveAll}>Archive pack</Button>
          )}
        </div>
      </CardHeader>
      {isActive && (
        <CardContent>
          <PackActivator packId={pack.id} />
          <PackSummaryStrip workspaceId={pack.workspace_id} packId={pack.id} />
          <PackChecklistBlock workspaceId={pack.workspace_id} packId={pack.id} />
          <div className="mb-3 rounded-md border border-cyan-400/20 bg-cyan-400/5 px-3 py-2 text-xs text-muted-foreground" data-testid={`ops-pack-review-guidance-${pack.id}`}>
            Review {liveTotal} asset{liveTotal === 1 ? "" : "s"} below. Guided Review may show only the items currently eligible for its stage; this list remains the complete campaign inventory.
          </div>
          <div className="divide-y divide-border/40">
            {assets.map((a, index) => {
              // Patch 37.4.18 — fall back to asset.id when
              // permanent_asset_id is scrubbed (non-admin viewers) so
              // testids never collide as `ops-asset-row-null`.
              const testKey = a.permanent_asset_id || a.id;
              return (
              <div
                key={a.id}
                data-testid={`ops-asset-row-${testKey}`}
                className="py-2 text-xs"
              >
                <div className="flex items-center gap-2">
                  <input
                    data-testid={`ops-asset-checkbox-${testKey}`}
                    type="checkbox"
                    checked={selectedIds.has(a.id)}
                    onChange={(e) => onSelectAsset(a.id, e.target.checked)}
                    aria-label={`Bulk-select ${a.content_type}`}
                  />
                  <BatchRenderCheckbox asset={a} workspaceId={pack.workspace_id} />
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-md px-2 py-2 text-left hover:bg-secondary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
                    onClick={() => openAssetId === a.id ? onCloseAsset() : onOpenAsset(a.id)}
                    aria-expanded={openAssetId === a.id}
                    aria-controls={`ops-asset-inline-${testKey}`}
                  >
                    <span className="min-w-0">
                      {a.permanent_asset_id ? (
                        <span className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{a.permanent_asset_id}</span>
                      ) : null}
                      <span className="block truncate font-medium">{getAssetLabel(a)}</span>
                    </span>
                    <span
                      data-testid={`ops-asset-status-${testKey}`}
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_META[a.status as AssetStatus].classes}`}
                    >
                      {STATUS_META[a.status as AssetStatus].label}
                    </span>
                  </button>
                </div>
                {openAssetId === a.id ? (
                  <AssetInlineReview
                    id={`ops-asset-inline-${testKey}`}
                    asset={a}
                    index={index}
                    total={assets.length}
                    onPrevious={index > 0 ? () => onOpenAsset(assets[index - 1].id) : undefined}
                    onNext={index + 1 < assets.length ? () => onOpenAsset(assets[index + 1].id) : undefined}
                    onSkip={index + 1 < assets.length ? () => onOpenAsset(assets[index + 1].id) : onCloseAsset}
                    onClose={onCloseAsset}
                    onTransition={(to) => onTransition(a, to)}
                  />
                ) : null}
              </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function AssetInlineReview({
  id, asset, index, total, onPrevious, onNext, onSkip, onClose, onTransition,
}: {
  id: string;
  asset: GeneratedAsset;
  index: number;
  total: number;
  onPrevious?: () => void;
  onNext?: () => void;
  onSkip: () => void;
  onClose: () => void;
  onTransition: (to: AssetStatus) => Promise<void>;
}): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [asset.id]);
  return (
    <div ref={panelRef} id={id} className="mt-2 rounded-xl border border-cyan-400/25 bg-background/60 p-2" data-testid="ops-asset-inline-review">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 px-1">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Asset {index + 1} of {total}
        </span>
        <div className="flex flex-wrap gap-1">
          <Button size="sm" variant="ghost" disabled={!onPrevious} onClick={onPrevious}>Previous</Button>
          <Button size="sm" variant="ghost" onClick={onSkip}>Skip for now</Button>
          <Button size="sm" variant="outline" disabled={!onNext} onClick={onNext}>Next</Button>
        </div>
      </div>
      <AssetDrawer asset={asset} onClose={onClose} onTransition={onTransition} inline />
    </div>
  );
}

// Patch 35.3b · pushes the active pack id into the batch selection
// provider whenever this pack renders in its expanded state. Also
// clears selection when the pack collapses (via the provider's
// setActivePack side-effect).
function PackActivator({ packId }: { packId: string }): null {
  const { setActivePack } = useBatchSelection();
  useEffect(() => {
    setActivePack(packId);
    return () => setActivePack(null);
  }, [packId, setActivePack]);
  return null;
}

// Patch 35.3b · small render-batch checkbox next to the bulk-transition
// one. Only visible when the asset is a render-eligible current-head
// approved image prompt AND the lifecycle endpoint confirms
// ``primary_action === "render_image"``. Backend is the source of
// truth for eligibility — the client-side heuristic below is only a
// pre-filter to avoid firing a lifecycle query for non-image rows.
function BatchRenderCheckbox({
  asset, workspaceId,
}: {
  asset: GeneratedAsset;
  workspaceId: string;
}): JSX.Element | null {
  const { isSelected, toggle, atMax, count, max } = useBatchSelection();
  const isImagePromptSpec = (
    (asset.capability === "image_generation"
      || asset.content_type.startsWith("image_prompt_"))
    && asset.payload_maturity !== "rendered_deliverable"
    && !!asset.is_head
    && !asset.superseded
    && (asset.status as string) === "approved"
  );
  const lc = useQuery({
    queryKey: ["asset-lifecycle", workspaceId, asset.id, asset.status, asset.is_head, asset.superseded],
    queryFn: () => fetchAssetLifecycle(workspaceId, asset.id),
    enabled: isImagePromptSpec,
    staleTime: 10_000,
  });
  if (!isImagePromptSpec) return null;
  const eligible = lc.data?.primary_action === "render_image";
  if (!eligible) return null;
  const checked = isSelected(asset.id);
  const disabled = !checked && atMax;
  return (
    <label
      className={
        "flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground " +
        (disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer")
      }
      title={disabled ? `Maximum ${max} images per batch` : "Add to render batch"}
    >
      <input
        data-testid={`batch-checkbox-${asset.id}`}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => toggle(asset.id)}
        aria-label={`Add ${asset.content_type} to render batch (${count} of ${max} selected)`}
      />
      render
    </label>
  );
}

// Patch 35.3b · Campaign completion checklist rendered inline in the
// expanded pack card. Reads the same ``pack-summary`` query as the
// summary strip so a single network round-trip powers both surfaces.
function PackChecklistBlock({
  workspaceId, packId,
}: {
  workspaceId: string;
  packId: string;
}): JSX.Element | null {
  const q = useQuery({
    queryKey: ["pack-summary", workspaceId, packId],
    queryFn: () => fetchPackSummary(workspaceId, packId),
    enabled: !!workspaceId && !!packId,
    staleTime: 15_000,
  });
  const s = q.data;
  if (!s || !s.progress) return null;
  return (
    <div className="mb-3">
      <CampaignChecklist
        progress={s.progress}
        currentStage={s.current_stage}
        lastCompletedStage={s.last_completed_stage}
      />
    </div>
  );
}

function AssetDrawer({
  asset, onClose, onTransition, inline = false,
}: {
  asset: GeneratedAsset | null;
  onClose: () => void;
  onTransition: (to: AssetStatus) => Promise<void>;
  inline?: boolean;
}): JSX.Element {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const isAdmin = meQuery.data?.is_dev_user === true;
  const [editing, setEditing] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  useEffect(() => {
    setEditing(false);
    setDraftBody(asset?.body_markdown ?? "");
  }, [asset?.id, asset?.body_markdown]);
  const editMut = useMutation({
    mutationFn: async () => {
      if (!asset || !ws) throw new Error("Asset unavailable");
      return editAsset(ws.id, asset.id, draftBody);
    },
    onSuccess: (updated) => {
      toast.success("Asset updated");
      setEditing(false);
      qc.setQueriesData<GeneratedAsset[]>(
        { queryKey: ["ops-pack-assets"] },
        (current) => current?.map((item) => item.id === updated.id ? updated : item),
      );
      void qc.invalidateQueries({ queryKey: ["ops-pack-assets"] });
    },
    onError: (e: Error) => toast.error(e.message || "Edit failed"),
  });
  if (!asset || !ws) {
    return (
      <div data-testid="ops-asset-drawer-empty" className="hidden lg:block">
        <Card className="sticky top-24 border-dashed">
          <CardContent className="p-6 text-center text-xs text-muted-foreground">
            Select an asset to review it here.
          </CardContent>
        </Card>
      </div>
    );
  }
  // Patch 35.1b · A head is a rendered image only when write-time
  // validation classified it as ``rendered_deliverable`` AND at least
  // one media file is attached. Non-head or superseded rows are never
  // shown here — the drawer always renders the head via ``fetchPackAssets``.
  const isRenderedImage =
    !!asset.is_head &&
    !asset.superseded &&
    asset.payload_maturity === "rendered_deliverable" &&
    (asset.media_file_ids?.length ?? 0) > 0 &&
    asset.capability === "image_generation";
  // Patch 35.2b-ii · Rendered video head: same rendered_deliverable
  // gate plus a video-family capability.
  const isRenderedVideo =
    !!asset.is_head &&
    !asset.superseded &&
    asset.payload_maturity === "rendered_deliverable" &&
    (asset.media_file_ids?.length ?? 0) > 0 &&
    (asset.capability === "video_generation" || asset.capability === "avatar_video");
  const isImagePromptSpec =
    asset.payload_maturity === "specification" ||
    (!!asset.content_type?.startsWith("image_prompt_") && !isRenderedImage);
  // Video production kit *spec* (v1) — eligible for a fresh render.
  const isVideoKitSpec =
    asset.content_type === "video_production_kit" &&
    !isRenderedVideo;
  return (
    <div className={inline ? "" : "sticky top-24"} data-testid="ops-asset-drawer" data-inline={inline ? "true" : "false"}>
      <Card className="border-border/70 bg-card/80 backdrop-blur">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div
                className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
                data-testid="ops-asset-drawer-permanent-id"
              >
                {(asset.permanent_asset_id ?? asset.id.slice(0, 12))} · v{asset.version_number}
              </div>
              <CardTitle data-testid="ops-asset-drawer-title" className="text-base">
                {getAssetLabel(asset)}
              </CardTitle>
              <CardDescription className="text-[10px]">
                {ws.profile.display_name} · pack {asset.campaign_pack_id?.slice(0, 8) ?? "—"} · brief {asset.creative_brief_id?.slice(0, 8) ?? "—"} · brain {(asset.brand_brain_version ?? "").slice(0, 10)}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          </div>
          <Badge className="mt-2 w-fit" variant="outline">
            <span className={`rounded px-2 py-0.5 text-[10px] font-medium ${STATUS_META[asset.status as AssetStatus].classes}`}>
              {STATUS_META[asset.status as AssetStatus].label}
            </span>
          </Badge>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="body">
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value="body">Body</TabsTrigger>
              <TabsTrigger value="versions">Versions</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>
            <TabsContent value="body" className="pt-3">
              {isRenderedVideo ? (
                <RenderedVideoBody workspaceId={ws.id} asset={asset} />
              ) : isRenderedImage ? (
                <RenderedAssetBody
                  workspaceId={ws.id}
                  asset={asset}
                />
              ) : (
                editing ? (
                  <div className="space-y-2">
                    <textarea
                      data-testid="ops-asset-edit-body"
                      className="min-h-64 w-full rounded-md border border-border bg-background p-3 text-sm"
                      value={draftBody}
                      onChange={(e) => setDraftBody(e.target.value)}
                    />
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraftBody(asset.body_markdown ?? ""); }}>Cancel</Button>
                      <Button size="sm" disabled={editMut.isPending || !draftBody.trim()} onClick={() => editMut.mutate()}>
                        {editMut.isPending ? "Saving…" : "Save changes"}
                      </Button>
                    </div>
                  </div>
                ) : (
                <pre
                  data-testid="ops-asset-drawer-body-pre"
                  className="max-h-64 overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-secondary/40 p-3 font-sans text-xs"
                >
                  {asset.body_json ? JSON.stringify(asset.body_json, null, 2) : asset.body_markdown ?? asset.image_prompt ?? "(empty)"}
                </pre>
                )
              )}
              {!isRenderedVideo && !isRenderedImage && !!asset.body_markdown && !editing ? (
                <Button data-testid="ops-asset-edit" className="mt-3" size="sm" variant="outline" onClick={() => setEditing(true)}>Edit</Button>
              ) : null}
              {isVideoKitSpec && (asset.status as string) === "approved" && (
                <div className="mt-3 space-y-2">
                  <HeroImagePicker
                    workspaceId={ws.id}
                    assetId={asset.id}
                    currentHeroAssetId={(asset.body_json as { hero_asset_id?: string })?.hero_asset_id ?? null}
                    currentHeroVersion={(asset.body_json as { hero_asset_version?: number })?.hero_asset_version ?? null}
                  />
                  <VideoRenderController
                    workspaceId={ws.id}
                    assetId={asset.id}
                    canRender={isAdmin}
                  />
                </div>
              )}
              {isImagePromptSpec ? (
                <RenderImageSpecificationButton asset={asset} workspaceId={ws.id} />
              ) : (
                <>
                  <LifecycleActions
                    workspaceId={ws.id}
                    workspaceTimezone={(ws as unknown as { time_zone?: string }).time_zone || "UTC"}
                    asset={asset}
                    onTransition={onTransition}
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <RegenerateButton asset={asset} workspaceId={ws.id} />
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="versions" className="pt-3">
              <VersionsTab workspaceId={ws.id} permanentAssetId={asset.permanent_asset_id ?? ""} />
            </TabsContent>

            <TabsContent value="history" className="pt-3">
              <HistoryTab workspaceId={ws.id} assetId={asset.id} permanentAssetId={asset.permanent_asset_id ?? ""} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function VersionsTab({ workspaceId, permanentAssetId }: { workspaceId: string; permanentAssetId: string }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["ops-asset-versions", permanentAssetId],
    queryFn: () => listVersions(workspaceId, permanentAssetId),
    enabled: !!permanentAssetId,
  });
  const restoreMut = useMutation({
    mutationFn: (assetId: string) => restoreVersion(workspaceId, assetId),
    onSuccess: () => {
      toast.success("Version restored");
      void qc.invalidateQueries({ queryKey: ["ops-asset-versions", permanentAssetId] });
      void qc.invalidateQueries({ queryKey: ["ops-pack-assets"] });
    },
  });
  const [compareOpen, setCompareOpen] = useState(false);
  if (q.isLoading) return <Skeleton className="h-24 w-full" />;
  if (q.isError) {
    const err = q.error as unknown as { message?: string; detail?: unknown };
    return (
      <div
        data-testid="ops-versions-error"
        className="rounded-md border border-dashed border-destructive/60 bg-destructive/5 p-3 text-xs text-destructive"
      >
        Could not load versions.
        {err?.message ? <div className="mt-1 opacity-70">{err.message}</div> : null}
      </div>
    );
  }
  const versions = q.data ?? [];
  if (versions.length === 0) {
    return (
      <div
        data-testid="ops-versions-empty"
        className="rounded-md border border-dashed border-border/60 bg-background/40 p-3 text-xs text-muted-foreground"
      >
        No version history yet.
      </div>
    );
  }
  const renderableVersions = versions.filter(
    (v) => v.payload_maturity === "rendered_deliverable"
      && (v.media_file_ids?.length ?? 0) > 0,
  );
  return (
    <div className="space-y-3">
      {renderableVersions.length >= 2 && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {renderableVersions.length} rendered variants
          </span>
          <Button
            data-testid="ops-versions-compare-open"
            size="sm"
            variant="outline"
            onClick={() => setCompareOpen(true)}
          >
            Compare versions
          </Button>
        </div>
      )}
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {versions.map((v) => (
          <div
            key={v.id}
            data-testid={`ops-version-item-${v.version_number}`}
            className="flex items-center justify-between rounded-md border border-border/60 bg-background/40 p-2 text-xs"
          >
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                v{v.version_number} {v.is_head && "· head"}
                {v.payload_maturity === "rendered_deliverable" && " · rendered"}
              </div>
              <div className="line-clamp-1">
                {v.body_markdown?.slice(0, 60) ?? v.image_prompt?.slice(0, 60) ?? "(structured)"}
              </div>
              {/* Patch 35.1b — provider/model only rendered when the server
               *   returns them (admins). Non-admins receive nulls via the
               *   classification scrubber, keeping this line hidden. */}
              {v.provider && v.model && (
                <div
                  data-testid={`ops-version-provenance-${v.version_number}`}
                  className="text-[10px] text-muted-foreground"
                >
                  {v.provider} · {v.model}
                </div>
              )}
            </div>
            {!v.is_head && (
              <Button data-testid={`ops-version-restore-${v.version_number}`} size="sm" variant="outline" onClick={() => restoreMut.mutate(v.id)}>
                Restore
              </Button>
            )}
          </div>
        ))}
      </div>
      {compareOpen && renderableVersions.length >= 2 && (
        <CompareVersionsDialog
          workspaceId={workspaceId}
          versions={renderableVersions}
          onClose={() => setCompareOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Patch 35.1b · Compare Versions.
 *
 * Overlay dialog rendering thumbnail previews of every rendered
 * version side by side. Uses signed URLs — never storage keys.
 */
function CompareVersionsDialog({
  workspaceId, versions, onClose,
}: {
  workspaceId: string;
  versions: GeneratedAsset[];
  onClose: () => void;
}): JSX.Element {
  return (
    <div
      data-testid="ops-versions-compare-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-5xl overflow-y-auto rounded-lg border border-border/60 bg-card p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Compare rendered versions</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {versions.map((v) => (
            <div
              key={v.id}
              data-testid={`ops-versions-compare-tile-v${v.version_number}`}
              className="space-y-1 rounded-md border border-border/50 bg-background/40 p-2"
            >
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                <span>v{v.version_number}{v.is_head && " · head"}</span>
                <span>{new Date(v.created_at).toLocaleString()}</span>
              </div>
              <RenderedImagePreview
                workspaceId={workspaceId}
                assetId={v.id}
                altText={`Version ${v.version_number} preview`}
                className="max-h-56"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Patch 35.1b · Rendered asset body.
 *
 * Shows the inline preview plus the preserved "Source Prompt" beneath.
 * The source prompt block is intentionally collapsible so the founder
 * defaults to reviewing the finished image, not the prompt spec.
 */
function RenderedAssetBody({
  workspaceId, asset,
}: {
  workspaceId: string;
  asset: GeneratedAsset;
}): JSX.Element {
  const [promptOpen, setPromptOpen] = useState(false);
  return (
    <div className="space-y-3">
      <RenderedImagePreview
        workspaceId={workspaceId}
        assetId={asset.id}
        altText={`${asset.content_type} rendered preview`}
      />
      <button
        data-testid="ops-source-prompt-toggle"
        className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
        onClick={() => setPromptOpen((prev) => !prev)}
      >
        Source prompt {promptOpen ? "▾" : "▸"}
      </button>
      {promptOpen && (
        <pre
          data-testid="ops-source-prompt-body"
          className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-secondary/40 p-3 font-sans text-[11px] text-muted-foreground"
        >
          {asset.image_prompt || asset.body_markdown || "(prompt preserved on the parent version)"}
        </pre>
      )}
    </div>
  );
}

/**
 * Patch 35.2b-ii · Rendered video head body.
 *
 * Shows the inline ``<video>`` player plus the preserved production
 * kit spec beneath it, collapsible so the founder defaults to the
 * finished video, not the kit JSON.
 */
function RenderedVideoBody({
  workspaceId, asset,
}: {
  workspaceId: string;
  asset: GeneratedAsset;
}): JSX.Element {
  const [kitOpen, setKitOpen] = useState(false);
  const sourceKit = (asset.body_json as { source_kit?: unknown })?.source_kit;
  return (
    <div className="space-y-3">
      <RenderedVideoPreview workspaceId={workspaceId} assetId={asset.id} />
      <button
        data-testid="ops-source-kit-toggle"
        className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
        onClick={() => setKitOpen((prev) => !prev)}
      >
        Source production kit {kitOpen ? "▾" : "▸"}
      </button>
      {kitOpen && (
        <pre
          data-testid="ops-source-kit-body"
          className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-secondary/40 p-3 font-sans text-[11px] text-muted-foreground"
        >
          {sourceKit
            ? JSON.stringify(sourceKit, null, 2)
            : "(kit preserved on the parent version)"}
        </pre>
      )}
    </div>
  );
}

/**
 * Patch 35.1b · Regenerate.
 *
 * For image_prompt_* / image_generation content types this posts to the
 * media engine render endpoint, which creates the next version head
 * via the same permanent asset id chain. For every other content type
 * it falls back to the existing AI Studio regenerate call.
 *
 * The workflow explicitly does NOT auto-approve — the founder must
 * still hit Approve to move the new head into the approved bucket.
 */
function PackSummaryStrip({
  workspaceId, packId,
}: {
  workspaceId: string; packId: string;
}): JSX.Element | null {
  const q = useQuery({
    queryKey: ["pack-summary", workspaceId, packId],
    queryFn: () => fetchPackSummary(workspaceId, packId),
    enabled: !!workspaceId && !!packId,
    staleTime: 15_000,
  });
  const s = q.data;
  if (!s || s.assets_total === 0) return null;
  const chips: Array<{ key: string; label: string; count: number; testId: string }> = [];
  if (s.awaiting_review > 0)
    chips.push({ key: "aw", label: "awaiting review", count: s.awaiting_review, testId: "pack-summary-awaiting" });
  if (s.image_prompts_ready_to_render > 0)
    chips.push({ key: "ir", label: "images ready to render", count: s.image_prompts_ready_to_render, testId: "pack-summary-images-ready" });
  if (s.video_kits_needing_hero > 0)
    chips.push({ key: "vh", label: "video needs starting frame", count: s.video_kits_needing_hero, testId: "pack-summary-video-hero" });
  if (s.video_kits_ready_to_render > 0)
    chips.push({ key: "vr", label: "videos ready to render", count: s.video_kits_ready_to_render, testId: "pack-summary-videos-ready" });
  if (s.rendered_assets_awaiting_final_approval > 0)
    chips.push({ key: "rf", label: "rendered awaiting final approval", count: s.rendered_assets_awaiting_final_approval, testId: "pack-summary-rendered-final" });
  if (s.vaulted_assets > 0)
    chips.push({ key: "vt", label: "in Vault", count: s.vaulted_assets, testId: "pack-summary-vaulted" });
  if (s.approved_not_vaulted > 0)
    chips.push({ key: "anv", label: "approved · vault pending", count: s.approved_not_vaulted, testId: "pack-summary-approved-not-vaulted" });
  if (s.scheduled_assets > 0)
    chips.push({ key: "sch", label: "scheduled", count: s.scheduled_assets, testId: "pack-summary-scheduled" });
  if (s.published_assets > 0)
    chips.push({ key: "pub", label: "published", count: s.published_assets, testId: "pack-summary-published" });
  return (
    <div
      data-testid="pack-summary-strip"
      className="mb-3 rounded-md border border-border/50 bg-secondary/25 p-3 text-xs"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div
          data-testid="pack-summary-recommended"
          className="font-medium"
        >
          {s.recommended_next_message}
        </div>
        <PackRecommendedCta summary={s} />
      </div>
      {chips.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <span
              key={c.key}
              data-testid={c.testId}
              className="rounded-md bg-background/70 px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground"
            >
              {c.count} {c.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


// Patch 35.3b · Executable Pack CTA. Reads recommended_next_action
// from the pack summary and dispatches into the corresponding UI
// affordance. Scheduling remains disabled per patch scope.
function PackRecommendedCta({ summary }: { summary: PackSummary }): JSX.Element | null {
  const { setActivePack, clear, toggle, activePackId } = useBatchSelection();
  const action = summary.recommended_next_action;
  const label = summary.recommended_next_label;
  if (!label || action === "review_complete") return null;

  const scrollToFilter = (status?: string) => {
    try {
      const el = document.querySelector<HTMLElement>('[data-testid="ops-filter-row"]');
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (status) {
        const btn = document.querySelector<HTMLButtonElement>(
          `[data-testid="ops-filter-status-${status}"]`,
        );
        btn?.click();
      }
    } catch {
      /* no-op */
    }
  };

  const handle = () => {
    switch (action) {
      case "review_pending":
        scrollToFilter("needs-review");
        return;
      case "approve_rendered":
        scrollToFilter("draft");
        return;
      case "render_images": {
        // Pre-fill the batch selection with up to 3 ready-to-render ids
        // (dispatched by the backend) so the founder can review the
        // preflight modal in one click.
        const ids = summary.recommended_next_asset_ids || [];
        clear();
        // Ensure the active pack is set (should already be — safety).
        if (!activePackId) setActivePack(null);
        for (const id of ids.slice(0, 3)) toggle(id);
        return;
      }
      case "choose_video_hero":
      case "render_videos":
        // Video actions live in the drawer — scroll to first video-kit row.
        try {
          const row = document.querySelector<HTMLElement>('[data-testid^="ops-asset-row-"]');
          row?.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch { /* no-op */ }
        return;
      case "register_vault":
        // No user action required — Vault backfill is a maintenance path.
        return;
    }
  };

  return (
    <Button
      size="sm"
      data-testid={`pack-cta-${action}`}
      onClick={handle}
    >
      {label}
    </Button>
  );
}


function RegenerateButton({
  asset, workspaceId,
}: {
  asset: GeneratedAsset;
  workspaceId: string;
}): JSX.Element {
  const qc = useQueryClient();
  const upgrade = useUpgradePrompt();
  const isRenderable =
    asset.capability === "image_generation" ||
    (asset.content_type && asset.content_type.startsWith("image_prompt_"));
  const isVideoRenderable =
    asset.content_type === "video_production_kit" ||
    asset.capability === "video_generation" ||
    asset.capability === "avatar_video";
  const mut = useMutation({
    mutationFn: async () => {
      if (isRenderable) {
        return renderImageAsset(workspaceId, asset.id);
      }
      if (isVideoRenderable) {
        // Video regeneration is asynchronous — the VideoRenderController
        // (rendered elsewhere in the drawer) drives it. This falls back
        // to the AI Studio regenerate call which will bump the kit spec
        // version, letting the founder render again from a fresh spec.
      }
      return regenerateAsset(workspaceId, asset.id);
    },
    onSuccess: () => {
      toast.success(isRenderable ? "New version rendered" : "Regenerated");
      void qc.invalidateQueries({ queryKey: ["ops-pack-assets"] });
      void qc.invalidateQueries({
        queryKey: ["ops-asset-versions", asset.permanent_asset_id],
      });
    },
    onError: (e: unknown) => {
      const err = e as { status?: number; code?: string; message?: string; details?: Record<string, unknown> };
      if (err.status === 402 && isRenderable) {
        upgrade.open({
          title: "Monthly image capacity reached",
          reason: err.message || "Upgrade, add image capacity, or wait for the monthly reset.",
          upgrade_target: err.details?.upgrade_target as string | undefined,
          addon_kind: "images",
        });
        return;
      }
      if (err.status === 402 || err.code === "text_generation_allowance_exhausted") {
        upgrade.open({
          title: "Monthly text capacity reached",
          reason: err.message || "Upgrade for unlimited text generation or wait for the monthly reset.",
          upgrade_target: err.details?.upgrade_target as string | undefined,
        });
        return;
      }
      toast.error(err.message || "Regenerate failed");
    },
  });
  return (
    <Button
      data-testid="ops-asset-drawer-regenerate"
      size="sm"
      variant="secondary"
      disabled={mut.isPending}
      onClick={() => mut.mutate()}
    >
      {mut.isPending ? "Rendering…" : "Regenerate"}
    </Button>
  );
}

function RenderImageSpecificationButton({
  asset, workspaceId,
}: {
  asset: GeneratedAsset;
  workspaceId: string;
}): JSX.Element {
  const qc = useQueryClient();
  const upgrade = useUpgradePrompt();
  const mut = useMutation({
    mutationFn: () => renderImageAsset(workspaceId, asset.id),
    onSuccess: () => {
      toast.success("Image rendered. Review the final image before approving.");
      void qc.invalidateQueries({ queryKey: ["ops-pack-assets"] });
    },
    onError: (error: unknown) => {
      const err = error as { status?: number; code?: string; message?: string };
      if (err.status === 402 || err.code === "premium_images_allowance_exhausted") {
        upgrade.open({
          title: "Monthly image capacity reached",
          reason: err.message || "Upgrade, add image capacity, or wait for the monthly reset.",
          addon_kind: "images",
        });
        return;
      }
      toast.error(err.message || "Image render failed");
    },
  });
  return (
    <Button
      data-testid="ops-render-image-specification"
      className="mt-3"
      disabled={mut.isPending}
      onClick={() => mut.mutate()}
    >
      {mut.isPending ? "Rendering…" : "Render image"}
    </Button>
  );
}

function HistoryTab({ workspaceId, assetId }: { workspaceId: string; assetId: string; permanentAssetId?: string }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["ops-asset-history", assetId],
    queryFn: () => getAssetHistory(workspaceId, assetId),
    enabled: !!assetId,
  });
  const [commentType, setCommentType] = useState<CommentType>("internal");
  const [body, setBody] = useState("");
  const addMut = useMutation({
    mutationFn: () => addComment(workspaceId, assetId, { type: commentType, body }),
    onSuccess: () => {
      setBody("");
      toast.success("Comment added");
      void qc.invalidateQueries({ queryKey: ["ops-asset-history", assetId] });
    },
  });
  return (
    <div className="space-y-3">
      <div className="max-h-64 space-y-2 overflow-y-auto">
        {(q.data ?? []).map((e: HistoryEvent) => (
          <div key={e.id} data-testid={`ops-history-item-${e.id}`} className="rounded-md border border-border/60 bg-background/40 p-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{e.type}</span>
              <span className="text-[10px] text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span>
            </div>
            <div className="mt-1 whitespace-pre-wrap">
              {typeof e.payload?.body === "string" ? String(e.payload.body) :
                e.payload?.from ? `${e.payload.from} → ${e.payload.to}` :
                e.payload?.version_number ? `version ${e.payload.version_number}` :
                JSON.stringify(e.payload)}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2 rounded-md border border-border/60 p-2">
        <div className="flex items-center gap-2">
          <select
            data-testid="ops-comment-type-select"
            value={commentType}
            onChange={(e) => setCommentType(e.target.value as CommentType)}
            className="rounded-md border border-border/60 bg-background px-2 py-1 text-xs"
          >
            <option value="internal">Internal</option>
            <option value="revision_note">Revision note</option>
            <option value="approval_note">Approval note</option>
            <option value="regeneration_note">Regeneration note</option>
          </select>
          <Input
            data-testid="ops-comment-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Leave a note…"
            className="text-xs"
          />
          <Button data-testid="ops-comment-submit" size="sm" disabled={!body || addMut.isPending} onClick={() => addMut.mutate()}>
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}
